import Vue from 'vue'
import DataStorage from './DataStorage'
import { urlBaseCoreAPI, urlBaseAppOrdit } from './urlBaseCoreAPI'
import isDevOrdit from './AppOrdit'
import store from '@/modules/store'
import throttle from 'lodash.throttle'

import qs from 'qs'

const callBackStorage = {
    apiErrorHandler: undefined,
    apiHandler: undefined,
}

const throttleRefresh = throttle(async (url) => {
    if (!Vue.prototype.$user?.authExpired || url.includes(Vue.prototype.$refreshEndpoint)) return
    return await Vue.prototype.$user.refreshToken()
}, 60000)

/**
 * API Client
 *
 * Composes API urls based on environment variables, handles all the REST verbs to the API, also includes basic
 * overrideable logic for handling global errors like validation and API errors.
 */
class Client {
    constructor() {
        this.dataStorage = new DataStorage()
        this.activeRequests = new Map()
    }

    /**
     * The base url host which can be used to compose api urls.
     *
     * @return {string} api host string
     */
    get urlBaseCoreAPI() {
        return urlBaseCoreAPI()
    }

    get urlBaseAppOrdit() {
        return urlBaseAppOrdit()
    }

    /**
     * The default request options used by all requests, can be overriden
     *
     * @return {object} request options
     */
    get defaultRequestOptions() {
        return {
            method: 'GET',
            headers: {},
        }
    }

    /**
     * Sets the response handler callback
     *
     */
    static setCallback(func) {
        callBackStorage.apiHandler = func
    }

    /**
     * Sets the error handler callback
     *
     */
    static setErrorCallback(func) {
        callBackStorage.apiErrorHandler = func
    }

    /**
     * Base api call which can be used to send requests to the specified url with the options given
     *
     * The function handles globally the most common cases like validation, authentication exceptions.
     *
     * @param {string} url
     * @param {object} options
     */
    async request(url, options = {}) {
        if (options?.pageLoader) store.dispatch('loading/start')
        await throttleRefresh(url)
        try {
            //const res = await this.call(this.getUrl(url), this.getOptions(options))
            const res = await fetch(this.getUrl(url), this.getOptions(options))
            let body = null
            if (res.status !== 204) body = await res.json()
            switch (res.status) {
                case 200:
                case 201:
                    await this.responseCallback()
                    return Promise.resolve(body)
                case 204:
                    await this.responseCallback()
                    return Promise.resolve()
                case 401:
                    this.errorCallback(res, body, url, options)
                    return Promise.reject(body)
                case 400: {
                    if (!res.url.includes('calculate')) this.errorCallback(res, body, url, options)
                    return Promise.reject(res.url.includes('calculate') ? { res, body } : body)
                }
                default:
                    this.errorCallback(res, body, url, options)
                    return Promise.reject(body)
            }
        } catch (e) {
            window.debug.warn(`Network error while connectiong to: ${url}`)
        } finally {
            store.dispatch('loading/finish')
        }
    }

    /**
     * Callback for requests
     */

    async responseCallback() {
        if (typeof callBackStorage?.apiHandler === 'function')
            return await callBackStorage.apiHandler()
        return
    }

    /**
     * Error callback for requests
     *
     * @param {string} res The returning response of the request
     * @param {object} body, The body of the response
     * @param {String} url, The target url of the request
     * @param {object} options, Extra options in object format for sending with the request
     */

    errorCallback(res, body, url, options) {
        if (typeof callBackStorage?.apiErrorHandler === 'function')
            callBackStorage.apiErrorHandler(res.status, body, res, {
                url: url,
                options: options,
            })
    }

    /**
     * Sends a GET request to the specified url and returns the promise which resolves to the results.
     *
     * @param {string} url The target url of the request
     * @param {object} extraOpts Extra options in object format for sending with the request
     */
    index(url, extraOpts = {}) {
        return this.request(url, Object.assign(this.defaultRequestOptions, extraOpts))
    }

    /**
     * Sends a POST request to the specified url with the specified payLoad and returns the promise which resolves to
     * the results.
     *
     * @param {string} url The target url of the request
     * @param {object} payLoad The payload to send with the request
     * @param {object} extraOpts Extra options in object format for sending with the request
     */
    store(url, payLoad = {}, extraOpts = {}) {
        return this.request(
            url,
            Object.assign(
                this.defaultRequestOptions,
                {
                    method: 'POST',
                    body: JSON.stringify(payLoad),
                },
                extraOpts
            )
        )
    }

    /**
     * Sends a PUT request to the specified url with the specified payLoad and returns the promise which resolves to
     * the results.
     *
     * @param {string} url The target url of the request
     * @param {object} payLoad The payload to send with the request
     * @param {object} extraOpts Extra options in object format for sending with the request
     */
    update(url, payLoad = {}, extraOpts = {}) {
        return this.request(
            url,
            Object.assign(
                this.defaultRequestOptions,
                {
                    method: 'PUT',
                    body: JSON.stringify(payLoad),
                },
                extraOpts
            )
        )
    }

    /**
     * Sends a DELETE request to the specified url and returns the promise which resolves to the results.
     *
     * @param {string} url The target url of the request
     * @param {object} extraOpts Extra options in object format for sending with the request
     */
    destroy(url, extraOpts = {}) {
        return this.request(
            url,
            Object.assign(
                Object.assign(this.defaultRequestOptions, { method: 'DELETE' }),
                extraOpts
            )
        )
    }

    /**
     * Return the API request
     *
     * @param {url} string Extended request url
     * @param {object} options Extended request options
     */
    call(url, options) {
        const key = `${options.method.toLowerCase()}:${url}${
            options.body ? `:${options.body.toString().replace(/\s+/g, '')}` : ''
        }`
        if (this.activeRequests.has(key)) {
            return this.activeRequests.get(key)
        }
        const request = fetch(url, options).finally(() => {
            this.activeRequests.delete(key)
        })
        this.activeRequests.set(key, request)
        return request
    }

    /**
     * Return the extended request url
     *
     * @param {string} url The target url of the request
     */

    getUrl(url) {
        const devOrditURL = isDevOrdit(url)
        if (!url.includes('translations')) {
            const locale = store.getters['locale']
            const langParams = qs.stringify({
                _locale: devOrditURL ? locale : undefined,
                lang: devOrditURL ? undefined : locale,
            })
            return `${devOrditURL ? this.urlBaseAppOrdit : this.urlBaseCoreAPI}${
                url.includes('?')
                    ? url.split('?').join(`?${langParams}&`)
                    : url.concat(`?${langParams}`)
            }`
        }
        return `${devOrditURL ? this.urlBaseAppOrdit : this.urlBaseCoreAPI}${url}`
    }

    /**
     * Return the extended request options
     *
     * @param {object} options Request options
     */

    getOptions(options) {
        delete options.pageLoader
        return Object.assign({ mode: 'cors', redirect: 'follow' }, options, {
            headers: Object.assign(
                {
                    Accept: 'application/json', // Fix API Platform
                    'Content-type': 'application/json',
                    ...(this.dataStorage.accessToken
                        ? { Authorization: `${this.dataStorage.headers}` }
                        : {}),
                },
                options.headers || {}
            ),
        })
    }
}

export default Client
